import React, { useContext, useEffect, useState } from 'react';
import { getUserData, uploadStudentOpinion } from '../../api/user-api';
import Student from '../../model/Student';
import { loginPage } from '../../store/fixed-routing';
import { Box, Button, Divider, Grid, Tab, Tabs, Typography, useTheme } from '@mui/material';
import AuthContext from '../../store/auth-context';
import OlympicsContext from '../../store/olympics-context';
import { useNavigate } from 'react-router-dom';
import SnackbarContext from '../../store/snackbar-context';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import Loader from '../../ui/Loader';
import UserData from './UserData';
import UserResults from './UserResults';
import UserQuestionnaire from './UserQuestionnaire';

type UserDataTextProps = {
	title?: string;
	text?: string;
	fullwidth?: boolean;
};
export const UserDataText = ({ title, text, fullwidth }: UserDataTextProps) => {
	const { palette } = useTheme();

	return (
		<Grid
			item
			container
			xs={fullwidth ? 12 : 6}
		>
			<Typography
				sx={{
					color: palette.primary.main,
					fontSize: '18px !important',
				}}
			>
				{title && (
					<Box
						component={'span'}
						sx={{
							fontWeight: 600,
						}}
					>
						{title}
					</Box>
				)}
				{text}
			</Typography>
		</Grid>
	);
};

const UserPanel = () => {
	const { palette } = useTheme();
	const { logout, token } = useContext(AuthContext);
	const { olympics, settingsVersion } = useContext(OlympicsContext);
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const { setMsg } = useContext(SnackbarContext);
	const [student, setStudent] = useState<Student | null>(null);

	const downloadData = () => {
		setLoading(true);
		getUserData(token)
			.then((response) => {
				if (response.status === 200) {
					setStudent(Student.fromApiResponse(response.data));
				} else {
					setMsg({ msg: response.data });
				}
				setLoading(false);
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					const msg = exception?.response?.data?.message
						? exception?.response?.data?.message
						: 'Błąd serwera. Spróbuj za chwilę';
					setMsg({ msg });
				}
				setLoading(false);
			});
	};

	useEffect(() => {
		downloadData();
	}, []);

	useEffect(() => {
		if (settingsVersion > 0) {
			downloadData();
		}
	}, [settingsVersion]);

	return (
		<Grid
			item
			container
			justifyContent={'start'}
			height={'100%'}
			width={'100%'}
			maxWidth={'650px'}
			flexDirection={'column'}
			gap={'24px'}
		>
			<Grid
				item
				container
				justifyContent={'space-between'}
			>
				<Typography
					variant={'h3'}
					fontWeight={'500'}
					color={palette.primary.main}
				>
					Edycja {olympics?.edition}
				</Typography>
				<Button
					size={'small'}
					startIcon={<LockOpenIcon />}
					onClick={() => logout()}
					sx={{
						fontSize: '14px',
					}}
				>
					Wyloguj
				</Button>
			</Grid>
			<Divider
				variant={'fullWidth'}
				sx={{
					backgroundColor: palette.primary.main,
					borderBottomWidth: '3px',
				}}
			/>
			{loading && <Loader />}
			{!loading && (
				<>
					<UserData student={student} />
					{student?.specialCertificate && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
							<Typography
								fontWeight={600}
								color={palette.primary.main}
							>
								Skan opinii poradni
							</Typography>
							<Typography variant='body2'>
								Masz zaznaczone orzeczenie lub opinię o dysgrafii. Możesz wgrać jeden lub więcej skanów (PDF/JPG/PNG,
								maks. 10 MB każdy) — np. dwie strony jako dwa zdjęcia. Nauczyciel i sekretarz mają wgląd; Ty widzisz tu
								listę załączonych plików.
							</Typography>
							{(student.opinionScan?.files?.length ?? 0) > 0 && (
								<Box>
									<Typography
										variant='body2'
										fontWeight={600}
									>
										Załączone pliki:
									</Typography>
									<ul style={{ margin: '4px 0 8px', paddingLeft: 20 }}>
										{student.opinionScan!.files.map((f, idx) => (
											<li key={f.id || idx}>
												<Typography
													variant='body2'
													component='span'
												>
													{f.originalName || f.storedName || `Plik ${idx + 1}`}
												</Typography>
											</li>
										))}
									</ul>
								</Box>
							)}
							<input
								type='file'
								multiple
								accept='.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png'
								onChange={async (e) => {
									const picked = e.target.files ? Array.from(e.target.files) : [];
									if (picked.length === 0 || !token) return;
									try {
										await uploadStudentOpinion(token, picked);
										setMsg({
											msg: picked.length > 1 ? `Zapisano ${picked.length} pliki.` : 'Opinia została zapisana.',
											severity: 'success',
										});
										downloadData();
									} catch {
										setMsg({ msg: 'Nie udało się wgrać pliku.', severity: 'error' });
									}
									e.target.value = '';
								}}
							/>
						</Box>
					)}
					{(() => {
						const finalistPts = olympics?.thirdStagePoints?.partA?.finalistPoints ?? 0;
						const meetsFinalistPoints = (student?.result?.thirdAStagePoints ?? 0) >= finalistPts;
						const isForeignDistrict = (student?.district?.name || '').toLowerCase() === 'zagranica';
						/** Zagranica: dostęp do ankiety ustnej cz. III bez progu „finalisty”, o ile kwalifikacja do IIIA. */
						const foreignOralEligible = isForeignDistrict && Boolean(student?.result?.thirdAStageQualified);
						const showQuestionnaire =
							Boolean(student?.result?.secondBStageQualified) &&
							Boolean(olympics?.secondBStageQualificationPublished) &&
							Boolean(olympics?.thirdStageQuestionnaireEnabled) &&
							(meetsFinalistPoints || foreignOralEligible);
						return showQuestionnaire ? <UserQuestionnaire student={student} /> : null;
					})()}
					{student?.approved && !olympics?.isStudentRegisterEnabled && <UserResults student={student} />}
				</>
			)}
		</Grid>
	);
};

export default UserPanel;
